import './styles/graphic.scss';
import { csv } from 'd3-fetch';
import { scaleBand, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { transition } from 'd3-transition';
import googleSheetData from './data/data.json';

// Extend selection prototype to include transition
select.prototype.transition = transition;

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    width,
    height,
    margins,
  } = props;

  // Common styling constants
  const commonStyles = {
    fontFamily: 'CNN, Arial, sans-serif',
    fontSize: isMobile ? 10 : 12, // Smaller text on mobile
    axisTextColor: '#B1B1B1',
    labelColor: '#6E6E6E',
  };

  // Responsive values
  const responsive = {
    legendY: isMobile ? -70 : -55,
    padding: isMobile ? 0.005 : 0.05,
    yPadding: isMobile ? 0.1 : 0.05, // Padding to create gaps: ~1px on mobile, ~2px on desktop
    timeTicks: isMobile ? ['00:00', '06:00', '12:00', '18:00', '23:00'] : ['00:00', '06:00', '12:00', '18:00', '23:00'],
    xAxisLabel: { y: isMobile ? initialHeight - 10 : initialHeight }, // Moved down on desktop
    yAxisLabel: { x: isMobile ? 12 : 18, y: isMobile ? 98 : 80 }, // Moved down further on desktop, and on mobile
    yTickLabelOffset: isMobile ? -8 : -10, // Responsive offset for y-axis tick labels
    yTickLength: isMobile ? -5 : -6, // Responsive length for y-axis tick lines
  };

  // Generate half-hourly time intervals
  const generateHalfHourlyTimes = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
      times.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  // Normalize time string to ensure leading zeros (e.g., "9:00" -> "09:00")
  const normalizeTime = (timeStr) => {
    if (!timeStr) return timeStr;
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return timeStr;
  };

  // Helper functions for common patterns
  const createStyledText = (parent, className = '', x = 0, y = 0, text = '', options = {}) => parent.append('text')
    .attr('class', className)
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', options.textAnchor || 'start')
    .attr('font-size', options.fontSize || commonStyles.fontSize)
    .attr('font-family', commonStyles.fontFamily)
    .attr('font-weight', options.fontWeight || 'normal')
    .style('fill', options.fill || commonStyles.axisTextColor)
    .text(text);

  const createStyledRect = (parent, className = '', x = 0, y = 0, width = 0, height = 0, options = {}) => parent.append('rect')
    .attr('class', className)
    .attr('x', x)
    .attr('y', y)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', options.fill || 'none')
    .attr('stroke', options.stroke || 'none')
    .attr('stroke-width', options.strokeWidth || 0)
    .attr('opacity', options.opacity || 1)
    .attr('rx', options.rx || 0);

  const createBorders = (parent, x, y, width, height, className, strokeColor, strokeWidth = 1) => {
    // Left border
    createStyledRect(parent, className, x, y, strokeWidth, height, {
      stroke: strokeColor,
      strokeWidth,
    });
    // Right border
    createStyledRect(parent, className, x + width - strokeWidth, y, strokeWidth, height, {
      stroke: strokeColor,
      strokeWidth,
    });
  };

  // Helper function to format day label
  const getDayLabel = day => `December ${day}`;

  // Increase SVG height on desktop to accommodate label below
  const svgHeight = isMobile ? initialHeight : initialHeight + 8;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', svgHeight);

  // Create defs for gradients
  const defs = svg.append('defs');

  // Create reusable gradients for sunrise and sunset transitions
  const sunriseGradient = defs.append('linearGradient')
    .attr('id', 'sunrise-gradient')
    .attr('x1', '0%')
    .attr('x2', '100%')
    .attr('y1', '0%')
    .attr('y2', '0%');

  sunriseGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#4a6fa5')
    .attr('stop-opacity', 0.15);
  sunriseGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#F8C153')
    .attr('stop-opacity', 0.1);

  const sunsetGradient = defs.append('linearGradient')
    .attr('id', 'sunset-gradient')
    .attr('x1', '0%')
    .attr('x2', '100%')
    .attr('y1', '0%')
    .attr('y2', '0%');

  sunsetGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#F8C153')
    .attr('stop-opacity', 0.1);
  sunsetGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#4a6fa5')
    .attr('stop-opacity', 0.15);

  // Create main group for the heatmap
  const g = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // Create tooltip outside the render function
  select('body').selectAll('.tooltip').remove();
  const tooltip = select('body').append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('background', 'rgba(0, 0, 0, 0.9)')
    .style('color', 'white')
    .style('padding', '10px')
    .style('border-radius', '6px')
    .style('font-size', `${commonStyles.fontSize}px`)
    .style('font-family', commonStyles.fontFamily)
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 8px rgba(0,0,0,0.3)');
  // Define renderHeatmap function first
  function renderHeatmap(data, tooltip, sunData = []) {
    // Clear existing content
    g.selectAll('*').remove();

    // Filter out empty rows and get unique values for scales
    const validData = data.filter(d => d.day && d.time && d.value);

    // Generate all days 1-30 for december (even if there's no data)
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    // Generate all half-hourly times
    const allHalfHourlyTimes = generateHalfHourlyTimes();

    // Get times that actually have data, but ensure we include all half-hourly intervals
    const dataTimes = [...new Set(validData.map(d => d.time))].sort();
    const times = allHalfHourlyTimes; // Use all half-hourly intervals for complete x-axis

    // Create scales with responsive padding
    const xScale = scaleBand()
      .domain(times)
      .range([2, width]) // Add 2px padding from y-axis
      .padding(responsive.padding);

    // On desktop, add offset to move grid down (more space from top labels)
    const gridYOffset = isMobile ? 0 : 15;

    const yScale = scaleBand()
      .domain(days.map(d => d.toString()))
      .range([gridYOffset, height])
      .padding(responsive.yPadding);

    // Create a separate scale for daylight hours with same padding as main scale (to show gaps)
    const yScaleDaylight = scaleBand()
      .domain(days.map(d => d.toString()))
      .range([gridYOffset, height])
      .padding(responsive.yPadding); // Use same padding to show gaps between rows

    // Clear any existing daylight and nighttime hours bars first
    g.selectAll('.daylight-hours').remove();
    g.selectAll('.nighttime-hours').remove();

    // Add daylight hours background bars for december
    if (sunData.length > 0) {
      // Filter sun data for december 2025
      const decemberSunData = sunData.filter((d) => {
        const date = new Date(d.date);
        return date.getMonth() === 11 && date.getFullYear() === 2025; // Month is 0-indexed, december is 10
      });

      // Group sun data by day
      const sunDataByDay = {};
      decemberSunData.forEach((d) => {
        const day = d.date.getDate();
        if (!sunDataByDay[day]) {
          sunDataByDay[day] = [];
        }
        sunDataByDay[day].push(d);
      });

      // Create background rectangles for daylight hours
      days.forEach((dayNum) => {
        const dayStr = dayNum.toString();
        // Skip if this day doesn't have sun data
        if (!sunDataByDay[dayNum]) {
          return;
        }

        const daySunData = sunDataByDay[dayNum];

        // Get sunrise and sunset times for the day
        const { sunrise } = daySunData[0]; // Should only be one entry per day
        const { sunset } = daySunData[0];

        // Parse sunrise/sunset times
        const [sunriseHours, sunriseMinutes] = sunrise.split(':').map(Number);
        const [sunsetHours, sunsetMinutes] = sunset.split(':').map(Number);

        // Convert to decimal hours
        const sunriseDecimal = sunriseHours + sunriseMinutes / 60;
        const sunsetDecimal = sunsetHours + sunsetMinutes / 60;

        // Find the half-hour intervals that cover sunrise and sunset
        // Round sunrise down to nearest half-hour, sunset up to nearest half-hour
        const sunriseHalfHour = Math.floor(sunriseDecimal * 2) / 2;
        const sunsetHalfHour = Math.ceil(sunsetDecimal * 2) / 2;

        // Convert to time format for x-scale (half-hourly)
        const sunriseHour = Math.floor(sunriseHalfHour);
        const sunriseMin = (sunriseHalfHour % 1) === 0 ? '00' : '30';
        const sunsetHour = Math.floor(sunsetHalfHour);
        const sunsetMin = (sunsetHalfHour % 1) === 0 ? '00' : '30';

        const sunriseTime = `${sunriseHour.toString().padStart(2, '0')}:${sunriseMin}`;
        const sunsetTime = `${sunsetHour.toString().padStart(2, '0')}:${sunsetMin}`;

        const sunriseX = xScale(sunriseTime);
        const sunsetX = xScale(sunsetTime);
        const daylightWidth = sunsetX - sunriseX;
        const rowY = yScaleDaylight(dayStr);
        const rowHeight = yScaleDaylight.bandwidth();
        const nightStartX = xScale('00:00');
        const nightEndX = xScale('23:30');
        const nightEndXFull = nightEndX + xScale.bandwidth();


        // Non-daylight period 1: Before sunrise (from 00:00 to sunrise)
        // Use gradient for the last hour before sunrise for fade effect
        const fadeWidth = xScale.bandwidth() * 2; // 1 hour (2 half-hour segments)
        const fadeStartX = sunriseX - fadeWidth;

        if (!isNaN(sunriseX) && sunriseX > nightStartX) {
          // Solid nighttime before fade zone
          if (fadeStartX > nightStartX) {
            const solidNightWidth = fadeStartX - nightStartX;
            if (solidNightWidth > 0) {
              g.append('rect')
                .attr('x', nightStartX)
                .attr('y', rowY)
                .attr('width', solidNightWidth)
                .attr('height', rowHeight)
                .attr('fill', '#4a6fa5')
                .attr('opacity', 0.15)
                .attr('class', 'nighttime-hours');
            }
          }

          // Fade zone at sunrise (blue to yellow)
          if (fadeStartX >= nightStartX && sunriseX > fadeStartX) {
            g.append('rect')
              .attr('x', fadeStartX)
              .attr('y', rowY)
              .attr('width', sunriseX - fadeStartX)
              .attr('height', rowHeight)
              .attr('fill', 'url(#sunrise-gradient)')
              .attr('class', 'sunrise-fade');
          }
        }

        // Daylight period: Between sunrise and sunset
        if (!isNaN(daylightWidth) && daylightWidth > 0) {
          // Main daylight rectangle
          g.append('rect')
            .attr('x', sunriseX)
            .attr('y', rowY)
            .attr('width', daylightWidth)
            .attr('height', rowHeight)
            .attr('fill', '#F8C153')
            .attr('opacity', 0.1)
            .attr('class', 'daylight-hours');
        }

        // Non-daylight period 2: After sunset (from sunset to end of 23:30)
        // Use gradient for the first hour after sunset for fade effect
        const sunsetFadeEndX = sunsetX + fadeWidth;

        if (!isNaN(sunsetX) && sunsetX < nightEndX) {
          // Fade zone at sunset (yellow to blue)
          if (sunsetFadeEndX <= nightEndXFull) {
            g.append('rect')
              .attr('x', sunsetX)
              .attr('y', rowY)
              .attr('width', fadeWidth)
              .attr('height', rowHeight)
              .attr('fill', 'url(#sunset-gradient)')
              .attr('class', 'sunset-fade');

            // Solid nighttime after fade zone
            const solidNightStartX = sunsetFadeEndX;
            const solidNightWidth2 = nightEndXFull - solidNightStartX;
            if (solidNightWidth2 > 0) {
              g.append('rect')
                .attr('x', solidNightStartX)
                .attr('y', rowY)
                .attr('width', solidNightWidth2)
                .attr('height', rowHeight)
                .attr('fill', '#4a6fa5')
                .attr('opacity', 0.15)
                .attr('class', 'nighttime-hours');
            }
          } else {
            // If fade zone extends beyond grid, just use gradient for remaining space
            g.append('rect')
              .attr('x', sunsetX)
              .attr('y', rowY)
              .attr('width', nightEndXFull - sunsetX)
              .attr('height', rowHeight)
              .attr('fill', 'url(#sunset-gradient)')
              .attr('class', 'sunset-fade');
          }
        }
      });
    }
    // Create heatmap rectangles - only show black boxes for blackouts (value === 1)
    const rects = g.selectAll('.heatmap-rect')
      .data(validData.filter(d => d.value === '1' || d.value === 1), d => `${d.day}-${d.time}`) // Use key function for proper data binding
      .enter()
      .append('rect')
      .attr('class', 'heatmap-rect')
      .attr('x', d => xScale(d.time))
      .attr('y', d => yScale(d.day.toString()))
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('fill', '#1e3a5f') // Dark navy blue for blackouts
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5);

    // Add red outline rectangle around Nov 1st, 8pm and 8:30pm boxes
    const nov1Y = yScale('1');
    const time8X = xScale('14:00');
    const time830X = xScale('14:30');
    const boxWidth = xScale.bandwidth();
    const boxHeight = yScale.bandwidth();

    if (!isNaN(nov1Y) && !isNaN(time8X) && !isNaN(time830X)) {
      // Calculate the rectangle that encompasses both boxes
      const outlineX = time8X;
      const outlineY = nov1Y;
      const outlineWidth = (time830X + boxWidth) - time8X;

      // Add red outline rectangle
      g.append('rect')
        .attr('x', outlineX)
        .attr('y', outlineY)
        .attr('width', outlineWidth)
        .attr('height', boxHeight)
        .attr('fill', 'none')
        .attr('stroke', '#d32f2f')
        .attr('stroke-width', isMobile ? 2 : 3)
        .attr('class', 'highlight-outline');

      // Add annotation text
      const annotationX = (time8X + time830X + boxWidth) / 2; // Center between the two boxes
      const annotationY = nov1Y - 6; // Above the row

      createStyledText(g, 'annotation-text', annotationX, annotationY, '1 hour', {
        textAnchor: 'middle',
        fill: '#d32f2f',
        fontSize: isMobile ? 10 : 12,
      });

      // Add a line from annotation to the boxes
      g.append('line')
        .attr('x1', annotationX)
        .attr('y1', annotationY + 2)
        .attr('x2', annotationX)
        .attr('y2', nov1Y)
        .style('stroke', '#d32f2f')
        .style('stroke-width', isMobile ? 1 : 2)
        .style('stroke-dasharray', '3,3');
    }


    // Add legend for blackouts
    const legendSettings = {
      width: isMobile ? 70 : 100, // Longer boxes
      height: 15,
      x: -margins.left, // Start from SVG edge (accounting for g translation)
      y: responsive.legendY,
      gap: isMobile ? 20 : 20, // Increased gap on mobile for better spacing
    };

    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendSettings.x}, ${legendSettings.y})`);

    // Add black rectangle for blackout legend
    legend.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', legendSettings.width)
      .attr('height', legendSettings.height)
      .attr('fill', '#1e3a5f'); // Dark navy blue

    // Legend label
    createStyledText(legend, '', legendSettings.width / 2, -5, 'Blackouts', {
      textAnchor: 'middle',
      fontWeight: '700',
      fill: commonStyles.labelColor,
    });

    // Add daylight hours label
    const daylightStart = xScale('07:00');
    const daylightEnd = xScale('19:00');

    if (daylightStart !== undefined && daylightEnd !== undefined) {
      const daylightX = legendSettings.x + legendSettings.width + legendSettings.gap;

      // Add yellow background rectangle (same size as legend)
      createStyledRect(g, '', daylightX, legendSettings.y, legendSettings.width, legendSettings.height, {
        fill: '#F8C153',
        opacity: 0.2,
        rx: 3,
      });

      // Add "Daylight hours" text above the rectangle
      createStyledText(g, 'daylight-label', daylightX + legendSettings.width / 2, legendSettings.y - 5, 'Daylight', {
        textAnchor: 'middle',
        fontWeight: '700',
        fill: commonStyles.labelColor,
      });

      // Add nighttime hours legend box
      const nighttimeX = daylightX + legendSettings.width + legendSettings.gap;

      // Add blue background rectangle (same size as legend)
      createStyledRect(g, '', nighttimeX, legendSettings.y, legendSettings.width, legendSettings.height, {
        fill: '#4a6fa5',
        opacity: 0.15,
        rx: 3,
      });

      // Add "Nighttime hours" text above the rectangle
      createStyledText(g, 'nighttime-label', nighttimeX + legendSettings.width / 2, legendSettings.y - 5, 'Nighttime', {
        textAnchor: 'middle',
        fontWeight: '700',
        fill: commonStyles.labelColor,
      });
    }

    // Create custom x-axis instead of using D3 axis
    const { timeTicks } = responsive;


    // Add custom tick lines only for labeled times
    const timeLabels = {
      '00:00': 'Midnight',
      '06:00': '6 a.m.',
      '12:00': '12 p.m.',
      '18:00': '6 p.m.',
      '23:00': '11 p.m.',
    };

    timeTicks.forEach((time) => {
      const x = xScale(time) + xScale.bandwidth() / 2;

      // Add tick line at bottom
      g.append('line')
        .attr('class', 'x-tick')
        .attr('x1', x)
        .attr('x2', x)
        .attr('y1', height)
        .attr('y2', height + 6)
        .style('stroke', '#B1B1B1')
        .style('stroke-width', 1);

      // Add tick label at bottom
      createStyledText(g, 'x-tick-label-bottom', x, height + 20, timeLabels[time] || time, {
        textAnchor: 'middle',
      });

      // Add tick line at top
      const topTickOffset = isMobile ? -23 : -15; // Move tick lines higher too
      g.append('line')
        .attr('class', 'x-tick-top')
        .attr('x1', x)
        .attr('x2', x)
        .attr('y1', gridYOffset)
        .attr('y2', gridYOffset + topTickOffset)
        .style('stroke', '#B1B1B1')
        .style('stroke-width', 1);

      // Add tick label at top (for all labeled times)
      if (timeLabels[time]) {
        // Move labels higher: -25px on desktop, -20px on mobile
        const topLabelOffset = isMobile ? -27 : -20;
        createStyledText(g, 'x-tick-label-top', x, gridYOffset + topLabelOffset, timeLabels[time], {
          textAnchor: 'middle',
        });
      }
    });

    // Add y-axis labels - show all days on both mobile and desktop
    const dayTicks = days;

    dayTicks.forEach((day) => {
      const dayStr = day.toString();
      const y = yScale(dayStr) + yScale.bandwidth() / 2; // Center of the rectangle

      // Add tick label - positioned to the right of where the axis line would be
      createStyledText(g, 'y-tick-label', isMobile ? -1 : -1, y + 3, day, {
        textAnchor: 'end',
      });
    });

    // Add "Total" header label at the top of the totals column
    const totalsX = isMobile ? width + 5 : width + 10;
    // On mobile, position below timeline labels (which are at gridYOffset - 15) to avoid overlap
    // Position "Total" at gridYOffset - 8, so "hours" is at gridYOffset + 4, just below the labels
    const headerY = isMobile ? gridYOffset - 14 : -5; // Position above the grid

    // Split into two lines: "Total" and "hours" (both mobile and desktop)
    createStyledText(g, 'totals-header', totalsX, headerY, 'Total', {
      textAnchor: 'start',
      fontWeight: 'normal', // Match "Date" and "Time of day" labels
      fill: commonStyles.labelColor,
      fontSize: commonStyles.fontSize, // Use same font size as other labels
    });
    createStyledText(g, 'totals-header', totalsX, headerY + commonStyles.fontSize + 2, 'hours', {
      textAnchor: 'start',
      fontWeight: 'normal',
      fill: commonStyles.labelColor,
      fontSize: commonStyles.fontSize,
    });

    // Calculate and display total hours for each day at the end of each row
    dayTicks.forEach((day) => {
      const dayStr = day.toString();
      const dayBlackouts = validData.filter(d => (d.value === '1' || d.value === 1) && d.day === dayStr);
      const dayHoursDecimal = dayBlackouts.length * 0.5;
      // Format: show as "4" for whole numbers, "4.5" for decimals
      const dayHours = dayHoursDecimal % 1 === 0 ? `${dayHoursDecimal}` : `${dayHoursDecimal.toFixed(1)}`;
      const y = yScale(dayStr) + yScale.bandwidth() / 2;

      // Add total hours label at the end of each row (both mobile and desktop)
      createStyledText(g, 'day-hours-label', totalsX, y + 3, dayHours, {
        textAnchor: 'start',
        fontWeight: '400',
        fill: commonStyles.axisTextColor, // Match axis text color
        fontSize: isMobile ? 10 : 12, // Smaller on mobile to match base font size
      });
    });

    // Add axis labels after scales are defined
    // Center the x-axis label in the middle of the grid
    const gridCenterX = margins.left + (width / 2);
    createStyledText(svg, 'x-axis-label', gridCenterX, responsive.xAxisLabel.y, 'Time of day', {
      textAnchor: 'middle',
      fill: commonStyles.labelColor,
    });

    // Position "Date" label at the top of the y-axis
    createStyledText(svg, 'y-axis-label', responsive.yAxisLabel.x, responsive.yAxisLabel.y, 'Date', {
      textAnchor: 'middle',
      fill: commonStyles.labelColor,
    });
    // Add event handlers to the rectangles
    rects.each(function (d) {
      const rect = select(this);
      rect
        .on('mouseover', (event) => {
          // Get rectangle position relative to the viewport
          const rect = this.getBoundingClientRect();
          const tooltipWidth = 120;
          const tooltipHeight = 60;

          // Calculate position to appear next to the rectangle
          let left = rect.right + 10; // 10px to the right of the rectangle
          let top = rect.top + (rect.height / 2) - (tooltipHeight / 2); // Centered vertically

          // Adjust if tooltip would go off screen
          if (left + tooltipWidth > window.innerWidth) {
            left = rect.left - tooltipWidth - 10; // Show to the left instead
          }
          if (top < 0) {
            top = 10; // Keep it within viewport
          }
          if (top + tooltipHeight > window.innerHeight) {
            top = window.innerHeight - tooltipHeight - 10;
          }

          // Format time consistently (e.g., "9:00 a.m.", "9:30 a.m.")
          const formatTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            const ampm = hours < 12 ? 'a.m.' : 'p.m.';
            const minStr = `:${minutes.toString().padStart(2, '0')}`; // Always show minutes
            return `${hour12}${minStr} ${ampm}`;
          };

          // Format time that is half an hour later than the original time
          const formatTimePlusHalf = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            let newHours = hours;
            let newMinutes = minutes + 30;

            // Handle minute overflow
            if (newMinutes >= 60) {
              newMinutes -= 60;
              newHours += 1;
            }

            // Handle hour overflow (24-hour format)
            if (newHours >= 24) {
              newHours -= 24;
            }

            const hour12 = newHours === 0 ? 12 : newHours > 12 ? newHours - 12 : newHours;
            const ampm = newHours < 12 ? 'a.m.' : 'p.m.';
            const minStr = `:${newMinutes.toString().padStart(2, '0')}`;
            return `${hour12}${minStr} ${ampm}`;
          };


          tooltip
            .style('opacity', 0.95)
            .html(`
              <div style="font-weight: bold; margin-bottom: 4px;">${getDayLabel(+d.day)}</div>
              <div style="margin-bottom: 8px;">Blackout from <br />${formatTime(d.time)} to ${`${formatTimePlusHalf(d.time)}`}</div>
            `)
            .style('left', `${left}px`)
            .style('top', `${top}px`);
        })
        .on('mouseout', () => {
          tooltip.style('opacity', 0);
        });
    });

    // Add daylight borders on top of heatmap rectangles
    if (sunData.length > 0) {
      // Filter sun data for december 2025
      const decemberSunData = sunData.filter((d) => {
        const date = new Date(d.date);
        return date.getMonth() === 10 && date.getFullYear() === 2025; // Month is 0-indexed, december is 10
      });

      // Group sun data by day
      const sunDataByDay = {};
      decemberSunData.forEach((d) => {
        const day = d.date.getDate();
        if (!sunDataByDay[day]) {
          sunDataByDay[day] = [];
        }
        sunDataByDay[day].push(d);
      });

      // Create border rectangles for daylight hours
      days.forEach((dayNum) => {
        const dayStr = dayNum.toString();
        // Skip if this day doesn't have sun data
        if (!sunDataByDay[dayNum]) {
          return;
        }

        const daySunData = sunDataByDay[dayNum];

        // Get sunrise and sunset times for the day
        const { sunrise } = daySunData[0];
        const { sunset } = daySunData[0];

        // Parse sunrise/sunset times
        const [sunriseHours, sunriseMinutes] = sunrise.split(':').map(Number);
        const [sunsetHours, sunsetMinutes] = sunset.split(':').map(Number);

        // Convert to decimal hours
        const sunriseDecimal = sunriseHours + sunriseMinutes / 60;
        const sunsetDecimal = sunsetHours + sunsetMinutes / 60;

        // Find the half-hour intervals that cover sunrise and sunset
        const sunriseHalfHour = Math.floor(sunriseDecimal * 2) / 2;
        const sunsetHalfHour = Math.ceil(sunsetDecimal * 2) / 2;

        // Convert to time format for x-scale (half-hourly)
        const sunriseHour = Math.floor(sunriseHalfHour);
        const sunriseMin = (sunriseHalfHour % 1) === 0 ? '00' : '30';
        const sunsetHour = Math.floor(sunsetHalfHour);
        const sunsetMin = (sunsetHalfHour % 1) === 0 ? '00' : '30';

        const sunriseTime = `${sunriseHour.toString().padStart(2, '0')}:${sunriseMin}`;
        const sunsetTime = `${sunsetHour.toString().padStart(2, '0')}:${sunsetMin}`;

        const sunriseX = xScale(sunriseTime);
        const sunsetX = xScale(sunsetTime);
        const daylightWidth = sunsetX - sunriseX;
      });
    }
  }

  // Helper function to convert decimal time to HH:MM format
  const decimalToTime = (decimal) => {
    const hours = Math.floor(decimal * 24);
    const minutes = Math.round((decimal * 24 - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Load both datasets
  let parsedData = [];
  let sunData = [];

  Promise.all([
    Promise.resolve(googleSheetData), // Load from Google Sheets data tab
    csv('./kyiv_sunrise_sunset_2025.csv'),
  ]).then(([blackoutDataRaw, sunDataRaw]) => {
    // Parse data from Google Sheets - handle both 'date'/'day' and decimal/time string formats
    parsedData = blackoutDataRaw
      .filter(d => (d.date || d.day) && (d.time !== null && d.time !== undefined) && d.value) // Filter out empty rows (check time explicitly to handle 0)
      .map((d) => {
        const day = d.date || d.day; // Handle both 'date' and 'day' fields
        let timeStr = d.time;

        // Convert decimal time to HH:MM if needed
        if (typeof timeStr === 'number') {
          timeStr = decimalToTime(timeStr);
        }

        return {
          day: day.toString(),
          time: normalizeTime(timeStr), // Normalize time to ensure leading zeros
          value: d.value,
        };
      });

    // Parse sun data - keep all dates for december filtering
    sunData = sunDataRaw.map((d) => {
      const date = new Date(d.date);
      return {
        sunrise: d.sunrise,
        sunset: d.sunset,
        date,
      };
    });

    // Render with both datasets
    renderHeatmap(parsedData, tooltip, sunData);
  }).catch((error) => {
    // Silently handle data loading errors
  });
}
